import stationDataConfigApi from '@/api/config/stationDataConfig'
import NoData from '@/components/common/NoData'
import MapPositionStation, {
  ChangePositionData,
} from '@/components/google-map/MapPositionStation'
import { LocalPinIcon } from '@/components/icons'
import ManualQuestionIcon from '@/components/icons/ManualQuestionIcon'
import SensorIcon from '@/components/icons/SensorIcon'
import SoilIcon from '@/components/icons/SoilIcon'
import StationErrorIcon from '@/components/icons/StationErrorIcon'
import SetUpHeaderModal from '@/features/design-system/components/common/SetUpHeaderModal'
import SetUpLayout from '@/features/design-system/components/common/SetUpLayout'
import { NotifyService } from '@/helpers'
import {
  DATA_PAGINATION,
  SearchStation,
  SetupStation,
  UPDATE_SOIL_MOISTURE_DATA,
  UPDATE_STATION_DATA,
} from '@/types'
import { formatPosition } from '@/ultils'
import {
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  debounce,
} from '@mui/material'
import clsx from 'clsx'
import { memo, useEffect, useState, useMemo } from 'react'
import classes from './styles.module.scss'
import useTranslation from '@/hooks/useTranslation'
import { MARKER_TYPE } from '@/types/marker'

interface Props {
  interfaceId: number
  data?: DATA_PAGINATION<SetupStation>
  onClose: () => void
}

enum STEP {
  CLICK_GEOTAG = 0,
  MOVE_MARKER,
  CLICK_SAVE,
  CONTINUE_GEOTAGGING,
  END_TUTORIALS,
}

export const PinStationsOnMap = memo(
  ({ data, interfaceId, onClose }: Props) => {
    const { t } = useTranslation()

    const [step, setStep] = useState(
      localStorage.getItem('pinStationsStep') || STEP.CLICK_GEOTAG,
    )
    const [tab, setTab] = useState<string>('0')

    let navBarOneWidth: string
    let navBarSecondWidth: string

    useEffect(() => {
      // Lấy giá trị từ localStorage
      const storedStep = localStorage.getItem('pinStationsStep')
      if (storedStep !== null) {
        setStep(parseInt(storedStep)) // Chuyển đổi chuỗi thành số nguyên
      }
    }, [])

    useEffect(() => {
      if (tab) {
        setItemEdit(undefined)
      }
    }, [tab])

    useEffect(() => {
      // Lưu giá trị của step vào localStorage khi step thay đổi
      localStorage.setItem('pinStationsStep', String(step))
    }, [step])

    switch (step) {
      case STEP.CLICK_GEOTAG:
        navBarOneWidth = '25%'
        navBarSecondWidth = '75%'
        break
      case STEP.MOVE_MARKER:
        navBarOneWidth = '50%'
        navBarSecondWidth = '50%'
        break
      case STEP.CLICK_SAVE:
        navBarOneWidth = '75%'
        navBarSecondWidth = '25%'
        break
      case STEP.CONTINUE_GEOTAGGING:
        navBarOneWidth = '100%'
        navBarSecondWidth = '0%'
        break
      default:
        navBarOneWidth = '100%'
        navBarSecondWidth = '0%'
    }

    const [itemEdit, setItemEdit] = useState<SetupStation>()

    const [stations, setStations] = useState<SetupStation[]>([])

    const [soilMoisture, setSoilMoisture] = useState<SetupStation[]>([])
    const [flowSensor, setFlowSensor] = useState<SetupStation[]>([])

    const [isLoading, setIsLoading] = useState<boolean>(false)

    const [param] = useState<SearchStation>({
      PageSize: 0,
      interfaceId,
    })

    const [paramSoil] = useState<SearchStation>()

    useEffect(() => {
      const fetchData = async () => {
        const _data = await stationDataConfigApi
          .getListWithDetail(interfaceId, param)
          .then((res) => {
            return res.data.data
          })
          .catch((e) => {
            NotifyService.error(e)
            return [] as SetupStation[]
          })
        const newData = [..._data].map((it) => ({
          ...it,
          markertType: MARKER_TYPE.Valve_OFF,
        }))
        setStations(newData)
        setItemEdit(undefined)
      }

      const fetchSoilData = async () => {
        try {
          const res = await stationDataConfigApi.getListWithSoilMoisture(
            interfaceId,
            paramSoil,
          )
          const _data: SetupStation[] = Array.isArray(res.data) ? res.data : []
          const newData = [..._data].map((it) => ({
            ...it,
            markertType: MARKER_TYPE.Soil_Moisture_Sensor,
          }))
          setSoilMoisture(newData)
        } catch (e) {
          NotifyService.error(e)
          setSoilMoisture([]) // Set to empty array on error
        }
        setItemEdit(undefined)
      }

      const fetchFlowData = async () => {
        try {
          const res = await stationDataConfigApi.getListWithSensor(
            interfaceId,
            paramSoil,
          )
          const _data: SetupStation[] = Array.isArray(res.data) ? res.data : []
          const newData = [..._data].map((it) => ({
            ...it,
            markertType: MARKER_TYPE.Flow_Sensor,
          }))
          setFlowSensor(newData)
        } catch (e) {
          NotifyService.error(e)
          setFlowSensor([]) // Set to empty array on error
        }
        setItemEdit(undefined)
      }
      if (interfaceId) {
        fetchData()
        fetchSoilData()
        fetchFlowData()
      }
      //eslint-disable-next-line
    }, [interfaceId, param])

    useEffect(() => {
      if (step === 0) {
        setItemEdit(stations[0])
      }
    }, [step, stations])

    const getButtonStyles = (currentTab: string) => ({
      color: tab === currentTab ? '#fff' : '#004744',
      backgroundColor: tab === currentTab ? '#004744' : '#ebf1f0',
      '&:hover': {
        color: tab === currentTab ? '#fff' : '#004744',
        backgroundColor: tab === currentTab ? '#004744' : '#ebf1f0',
      },
    })

    const onChangePosition = debounce(async (data: ChangePositionData) => {
      if (!itemEdit || isLoading) return
      setIsLoading(true)
      if (tab === '0') {
        const body: UPDATE_STATION_DATA = {
          interfaceId: interfaceId,
          locationId: data.locationId,
          floorId: data.floorId,
          stationId: itemEdit.id,
          grsCoordinatedLatitude: data.grsCoordinatedLatitude,
          grsCoordinatedLongitude: data.grsCoordinatedLongitude,
          subAreaId: itemEdit.subAreaId,
        }
        await stationDataConfigApi
          .updateStations([body])
          .then(() => {
            const _stations = [...stations]
            const i = _stations.findIndex((it) => it.id === itemEdit.id)
            if (i === -1) return
            // _stations[i].siteId = data.siteId
            // _stations[i].siteName = data.siteName
            _stations[i].locationId = data.locationId
            _stations[i].locationName = data.locationName
            _stations[i].floorId = data.floorId
            _stations[i].grsCoordinatedLatitude = data.grsCoordinatedLatitude
            _stations[i].grsCoordinatedLongitude = data.grsCoordinatedLongitude
            // _stations[i].subAreaId = data.subAreaId
            setItemEdit(undefined)
            setStations(_stations)
            NotifyService.success(
              'The station has been geotagged successfully',
              { position: 'top-center' },
            )
          })
          .catch((e) => NotifyService.error(e, { position: 'top-center' }))
          .finally(() => setIsLoading(false))
      } else if (tab === '1') {
        const body: UPDATE_SOIL_MOISTURE_DATA = {
          interfaceId: interfaceId,
          locationId: data.locationId,
          floorId: data.floorId,
          sensorId: itemEdit.id,
          grsCoordinatedLatitude: data.grsCoordinatedLatitude,
          grsCoordinatedLongitude: data.grsCoordinatedLongitude,
          subAreaId: itemEdit.subAreaId,
        }
        await stationDataConfigApi
          .updateFlowSensor([body])
          .then(() => {
            const _flowSensor = [...flowSensor]
            const i = _flowSensor.findIndex((it) => it.id === itemEdit.id)
            if (i === -1) return
            // _flowSensor[i].siteId = data.siteId
            // _flowSensor[i].siteName = data.siteName
            _flowSensor[i].locationId = data.locationId
            _flowSensor[i].locationName = data.locationName
            _flowSensor[i].floorId = data.floorId
            _flowSensor[i].grsCoordinatedLatitude = data.grsCoordinatedLatitude
            _flowSensor[i].grsCoordinatedLongitude =
              data.grsCoordinatedLongitude
            // _flowSensor[i].subAreaId = data.subAreaId
            setItemEdit(undefined)
            setFlowSensor(_flowSensor)
            NotifyService.success(
              'The flow sensor has been geotagged successfully',
              { position: 'top-center' },
            )
          })
          .catch((e) => NotifyService.error(e, { position: 'top-center' }))
          .finally(() => setIsLoading(false))
      } else if (tab === '2') {
        const body: UPDATE_SOIL_MOISTURE_DATA = {
          interfaceId: interfaceId,
          locationId: data.locationId,
          floorId: data.floorId,
          sensorId: itemEdit.id,
          grsCoordinatedLatitude: data.grsCoordinatedLatitude,
          grsCoordinatedLongitude: data.grsCoordinatedLongitude,
          subAreaId: itemEdit.subAreaId,
        }
        await stationDataConfigApi
          .updateSoilMoisture([body])
          .then(() => {
            const _soilMoisture = [...soilMoisture]
            const i = _soilMoisture.findIndex((it) => it.id === itemEdit.id)
            if (i === -1) return
            // _soilMoisture[i].siteId = data.siteId
            // _soilMoisture[i].siteName = data.siteName
            _soilMoisture[i].locationId = data.locationId
            _soilMoisture[i].locationName = data.locationName
            _soilMoisture[i].floorId = data.floorId
            _soilMoisture[i].grsCoordinatedLatitude =
              data.grsCoordinatedLatitude
            _soilMoisture[i].grsCoordinatedLongitude =
              data.grsCoordinatedLongitude
            // _soilMoisture[i].subAreaId = data.subAreaId
            setItemEdit(undefined)
            setSoilMoisture(_soilMoisture)
            NotifyService.success(
              'The soil moisture has been geotagged successfully',
              { position: 'top-center' },
            )
          })
          .catch((e) => NotifyService.error(e, { position: 'top-center' }))
          .finally(() => setIsLoading(false))
      }
    }, 500)

    const onEditPosition = (item: SetupStation) => {
      if (itemEdit?.id === item.id) return
      setItemEdit(item)
    }

    const handleSkip = () => {
      setStep(STEP.END_TUTORIALS)
    }

    const handleTutorials = () => {
      setStep(STEP.CLICK_GEOTAG)
    }

    const render = () => {
      switch (step) {
        case STEP.CLICK_GEOTAG:
          return (
            <Stack className={classes.boxStepOne}>
              <Stack direction="row" width="100%">
                <Stack
                  className={classes.navBarOne}
                  sx={{ width: navBarOneWidth }}
                />
                <Stack
                  className={classes.navBarSecond}
                  sx={{ width: navBarSecondWidth }}
                />
              </Stack>
              <Box className={classes.bodyBox}>
                <Typography className={classes.stepIndicator}>
                  Step 1/4
                </Typography>
                <Typography className={classes.geoTagTitle}>
                  Geotag Decoders
                </Typography>
                <Typography className={classes.geoTagInstruction}>
                  Click on GeoTag button under the decoder list to start
                  geo-tagging.
                </Typography>
              </Box>
              <Stack
                className={classes.footerActionButtons}
                direction="row"
                spacing={2}
              >
                <Button variant="secondary" onClick={handleSkip}>
                  Skip
                </Button>
                <Button
                  variant="primary"
                  onClick={() => setStep(STEP.MOVE_MARKER)}
                >
                  Next
                </Button>
              </Stack>
            </Stack>
          )
        case STEP.MOVE_MARKER:
          return (
            <Stack className={classes.boxStepSecond}>
              <Stack direction="row" width="100%">
                <Stack
                  className={classes.navBarOne}
                  sx={{ width: navBarOneWidth }}
                />
                <Stack
                  className={classes.navBarSecond}
                  sx={{ width: navBarSecondWidth }}
                />
              </Stack>
              <Box className={classes.bodyBox}>
                <Typography className={classes.stepIndicator}>
                  Step 2/4
                </Typography>
                <Typography className={classes.geoTagTitle}>
                  Geotag Decoders
                </Typography>
                <Typography className={classes.geoTagInstruction}>
                  Move the marker on the map to the correct location of the
                  decoder
                </Typography>
                <Stack direction="row" justifyContent="space-between" mt="30px">
                  <Button
                    className={classes.btnBack}
                    onClick={() => setStep(STEP.CLICK_GEOTAG)}
                  >
                    Back
                  </Button>
                  <Stack
                    className={classes.footerActionButtons}
                    direction="row"
                    spacing={2}
                  >
                    <Button variant="secondary" onClick={handleSkip}>
                      Skip
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => setStep(STEP.CLICK_SAVE)}
                    >
                      Next
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          )
        case STEP.CLICK_SAVE:
          return (
            <Stack className={classes.boxStepThird}>
              <Stack direction="row" width="100%">
                <Stack
                  className={classes.navBarOne}
                  sx={{ width: navBarOneWidth }}
                />
                <Stack
                  className={classes.navBarSecond}
                  sx={{ width: navBarSecondWidth }}
                />
              </Stack>
              <Box className={classes.bodyBox}>
                <Typography className={classes.stepIndicator}>
                  Step 3/4
                </Typography>
                <Typography className={classes.geoTagTitle}>
                  Geotag Decoders
                </Typography>
                <Typography className={classes.geoTagInstruction}>
                  Click <span className={classes.txtSave}>Save</span>
                </Typography>
                <Stack direction="row" justifyContent="space-between" mt="30px">
                  <Button
                    className={classes.btnBack}
                    onClick={() => setStep(STEP.MOVE_MARKER)}
                  >
                    Back
                  </Button>
                  <Stack
                    className={classes.footerActionButtons}
                    direction="row"
                    spacing={2}
                  >
                    <Button variant="secondary" onClick={handleSkip}>
                      Skip
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => setStep(STEP.CONTINUE_GEOTAGGING)}
                    >
                      Next
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          )

        case STEP.CONTINUE_GEOTAGGING:
          return (
            <Stack className={classes.boxStepFour}>
              <Stack direction="row" width="100%">
                <Stack
                  className={classes.navBarOne}
                  sx={{ width: navBarOneWidth }}
                />
                <Stack
                  className={classes.navBarSecond}
                  sx={{ width: navBarSecondWidth }}
                />
              </Stack>
              <Box className={classes.bodyBox}>
                <Typography className={classes.stepIndicator}>
                  Step 4/4
                </Typography>
                <Typography className={classes.geoTagTitle}>
                  Geotag Decoders
                </Typography>
                <Typography className={classes.geoTagInstruction}>
                  Continue to geotag the rest of the decoders
                </Typography>
                <Stack direction="row" justifyContent="space-between" mt="30px">
                  <Button
                    className={classes.btnBack}
                    onClick={() => setStep(STEP.CLICK_SAVE)}
                  >
                    Back
                  </Button>
                  <Stack className={classes.footerActionButtons}>
                    <Button
                      variant="primary"
                      onClick={() => setStep(STEP.END_TUTORIALS)}
                    >
                      Done
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          )
      }
    }

    const handleChangeTab = (newTab: string) => {
      setTab(newTab)
    }

    const allStations = useMemo(() => {
      return [...stations, ...flowSensor, ...soilMoisture]
    }, [stations, flowSensor, soilMoisture])

    return (
      <Box className={classes.root}>
        <Stack direction="row" className={classes.leftBox}>
          <SetUpLayout
            fullWidth
            title="Pin stations on map"
            header={
              <SetUpHeaderModal
                title={
                  <Stack direction="row" justifyContent="space-between">
                    <Typography className={classes.txtStationHeader}>
                      {t('geotagDevice')}
                    </Typography>
                    <Stack>
                      <IconButton onClick={handleTutorials}>
                        <ManualQuestionIcon sx={{ color: 'text.main' }} />
                      </IconButton>
                    </Stack>
                  </Stack>
                }
                onCancel={onClose}
              />
            }
          >
            <Box className={classes.topBox}>
              <Stack direction="row" spacing={1}>
                <Button
                  className={classes.btnStatus}
                  startIcon={<StationErrorIcon />}
                  onClick={() => handleChangeTab('0')}
                  sx={getButtonStyles('0')}
                >
                  Stations
                </Button>
                <Button
                  className={classes.btnStatus}
                  startIcon={<SensorIcon sx={{ color: '#29aae1' }} />}
                  onClick={() => handleChangeTab('1')}
                  sx={getButtonStyles('1')}
                >
                  Flow Sensor
                </Button>

                <Button
                  className={classes.btnStatus}
                  startIcon={<SoilIcon sx={{ color: '#ffef98' }} />}
                  onClick={() => handleChangeTab('2')}
                  sx={getButtonStyles('2')}
                >
                  Soil Moisture
                </Button>
              </Stack>
            </Box>
            <Box
              className={classes.content}
              sx={{
                ...(step !== STEP.END_TUTORIALS && {
                  opacity: 0.5,
                  userSelect: 'none',
                  pointerEvents: 'none',
                }),
              }}
            >
              {tab === '0' && (
                <>
                  {stations && stations.length === 0 && (
                    <NoData text={t('noData')} />
                  )}
                  {stations && stations.length > 0 && (
                    <TableContainer id="scrollableDiv">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ minWidth: '30px' }}>ID</TableCell>
                            <TableCell sx={{ minWidth: '155px' }}>
                              Decoder Name
                            </TableCell>
                            <TableCell sx={{ minWidth: '118px' }}>
                              Area
                            </TableCell>
                            <TableCell sx={{ minWidth: '118px' }}>
                              Sub-Area
                            </TableCell>
                            <TableCell>GPS Coordinates</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {stations.map((row) => (
                            <TableRow
                              key={row.id}
                              className={clsx({
                                [classes.rowAction]: row.id === itemEdit?.id,
                              })}
                            >
                              <TableCell>{row.id}</TableCell>
                              <TableCell>{row.controller}</TableCell>
                              <TableCell>{row?.areaName}</TableCell>
                              <TableCell>{row?.subAreaName}</TableCell>
                              <TableCell>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  className={classes.positionBox}
                                  onClick={() => onEditPosition(row)}
                                >
                                  <Typography
                                    color="primary.contrastText"
                                    className={classes.positionText}
                                  >
                                    {row.grsCoordinatedLatitude &&
                                    row.grsCoordinatedLongitude
                                      ? formatPosition(
                                          new google.maps.LatLng(
                                            row.grsCoordinatedLatitude,
                                            row.grsCoordinatedLongitude,
                                          ),
                                        )
                                      : 'Pin on Map'}
                                  </Typography>
                                  <LocalPinIcon
                                    className={classes.positionIcon}
                                    sx={{ color: 'primary.contrastText' }}
                                  />
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}

              {tab === '1' && (
                <>
                  {flowSensor && flowSensor.length === 0 && (
                    <NoData text={t('noData')} />
                  )}
                  {flowSensor && flowSensor.length > 0 && (
                    <TableContainer id="scrollableDiv">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ minWidth: '155px' }}>
                              Description
                            </TableCell>
                            <TableCell>GPS Coordinates</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {flowSensor.map((row) => (
                            <TableRow
                              key={row.id}
                              className={clsx({
                                [classes.rowAction]: row.id === itemEdit?.id,
                              })}
                            >
                              <TableCell>{row.description}</TableCell>
                              <TableCell>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  className={classes.positionBox}
                                  onClick={() => onEditPosition(row)}
                                >
                                  <Typography
                                    color="primary.contrastText"
                                    className={classes.positionText}
                                  >
                                    {row.grsCoordinatedLatitude &&
                                    row.grsCoordinatedLongitude
                                      ? formatPosition(
                                          new google.maps.LatLng(
                                            row.grsCoordinatedLatitude,
                                            row.grsCoordinatedLongitude,
                                          ),
                                        )
                                      : 'Pin on Map'}
                                  </Typography>
                                  <LocalPinIcon
                                    className={classes.positionIcon}
                                    sx={{ color: 'primary.contrastText' }}
                                  />
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}
              {tab === '2' && (
                <>
                  {soilMoisture && soilMoisture.length === 0 && (
                    <NoData text={t('noData')} />
                  )}
                  {soilMoisture && soilMoisture.length > 0 && (
                    <TableContainer id="scrollableDiv">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ minWidth: '155px' }}>
                              Description
                            </TableCell>
                            <TableCell>GPS Coordinates</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {soilMoisture.map((row) => (
                            <TableRow
                              key={row.id}
                              className={clsx({
                                [classes.rowAction]: row.id === itemEdit?.id,
                              })}
                            >
                              <TableCell>{row.description}</TableCell>
                              <TableCell>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  onClick={() => onEditPosition(row)}
                                >
                                  <Typography
                                    color="primary.contrastText"
                                    className={classes.positionText}
                                  >
                                    {row.grsCoordinatedLatitude &&
                                    row.grsCoordinatedLongitude
                                      ? formatPosition(
                                          new google.maps.LatLng(
                                            row.grsCoordinatedLatitude,
                                            row.grsCoordinatedLongitude,
                                          ),
                                        )
                                      : 'Pin on Map'}
                                  </Typography>
                                  <LocalPinIcon
                                    className={classes.positionIcon}
                                    sx={{ color: 'primary.contrastText' }}
                                  />
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}
            </Box>
          </SetUpLayout>
        </Stack>

        <Stack sx={{ flex: '1', height: '100%', position: 'relative' }}>
          <Box
            className={classes.mapBox}
            sx={{
              ...(step !== STEP.END_TUTORIALS && {
                opacity: 0.5,
                userSelect: 'none',
                pointerEvents: 'none',
              }),
            }}
          >
            <MapPositionStation
              isFilterByFloor
              isCheckMarkertType
              interfaceId={interfaceId}
              station={itemEdit}
              onDone={onChangePosition}
              allStations={allStations}
            />
          </Box>
          <Box>{render()}</Box>
        </Stack>
      </Box>
    )
  },
)

export default PinStationsOnMap
